/**
 * Segmented Component - Theme-aware segmented control (button group)
 * ✅ Playbook Compliance: Theme-aware, WCAG 2.1 AA compliant, React.createElement
 */

const React = (globalThis as any).React;
const { useState } = React;

import { styles } from '../styles';

// ✅ Fix TypeScript error: Declare React types for type checking
type ReactNode = any;
type CSSProperties = any;

export interface SegmentedOption {
  label: ReactNode;
  value: string;
  disabled?: boolean;
  icon?: ReactNode;
}

export interface SegmentedProps {
  options: SegmentedOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: CSSProperties;
}

export function Segmented({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  disabled = false,
  size = 'default',
  style,
}: SegmentedProps) {
  // ✅ Fix TypeScript error: Explicitly type useState without type arguments
  const initialValue: string = defaultValue || options[0]?.value || '';
  const [internalValue, setInternalValue] = (useState as any)(initialValue);

  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = (newValue: string) => {
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const containerStyle: CSSProperties = {
    display: 'inline-flex',
    background: 'var(--input-bg)',
    borderRadius: '8px',
    padding: '4px',
    gap: '4px',
    ...style,
  };

  const buttonStyle = (option: SegmentedOption): CSSProperties => ({
    padding: size === 'small' ? '4px 12px' : size === 'large' ? '8px 20px' : '6px 16px',
    background: value === option.value ? 'var(--card-bg)' : 'transparent',
    color: value === option.value ? 'var(--main-text)' : 'var(--main-text-secondary)',
    border: 'none',
    borderRadius: '6px',
    cursor: (disabled || option.disabled) ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    opacity: (disabled || option.disabled) ? 0.5 : 1,
    transition: 'all 0.2s ease',
    fontWeight: value === option.value ? '500' : '400',
    boxShadow: value === option.value ? 'var(--shadow-sm)' : 'none',
  });

  return React.createElement('div', {
    role: 'group',
    'aria-label': 'Segmented control',
    style: containerStyle,
  },
    options.map((option) =>
      React.createElement('button', {
        key: option.value,
        onClick: () => !disabled && !option.disabled && handleChange(option.value),
        disabled: disabled || option.disabled,
        'aria-pressed': value === option.value,
        style: buttonStyle(option),
      },
        option.icon,
        option.label
      )
    )
  );
}

